import React from 'react';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { useTheme } from '../../context/ThemeContext';

const PrivacyPolicy = () => {
  const { isDarkMode } = useTheme();

  const pageBg = isDarkMode ? DARK_THEME.background : '#f3f8fa';
  const ink = isDarkMode ? DARK_THEME.textPrimary : '#0F2630';
  const muted = isDarkMode ? DARK_THEME.textSecondary : '#5b6b72';
  const line = isDarkMode ? DARK_THEME.border : '#e3eaed';
  const navy = isDarkMode ? DARK_THEME.primary : '#173D4D';
  const accent = isDarkMode ? DARK_THEME.primary : COLORS.primary;

  const s = {
    page: { backgroundColor: pageBg, color: ink, lineHeight: 1.65 },
    hero: {
      paddingTop: '120px',
      paddingBottom: '40px',
      paddingLeft: '20px',
      paddingRight: '20px',
      background: isDarkMode
        ? `linear-gradient(135deg, ${DARK_THEME.backgroundSecondary} 0%, ${DARK_THEME.background} 100%)`
        : `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
      textAlign: 'center',
    },
    heroTitle: { fontSize: 'clamp(28px, 5vw, 40px)', color: COLORS.white, fontWeight: 700, margin: '0 0 8px' },
    heroMeta: { color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', margin: 0 },
    wrap: { maxWidth: '860px', margin: '0 auto', padding: '40px 20px 80px' },
    p: { fontSize: '1rem', margin: '0 0 14px' },
    h2: { fontSize: '1.3rem', margin: '36px 0 12px', color: navy, fontWeight: 700 },
    h3: { fontSize: '1.05rem', margin: '22px 0 8px', color: ink, fontWeight: 600 },
    ul: { paddingLeft: '22px', margin: '0 0 14px' },
    li: { fontSize: '1rem', marginBottom: '6px' },
    a: { color: accent, textDecoration: 'none' },
    fill: {
      backgroundColor: isDarkMode ? `${DARK_THEME.primary}1A` : '#eafafb',
      border: `1px solid ${isDarkMode ? DARK_THEME.border : '#bfe9ef'}`,
      borderRadius: '12px',
      padding: '18px 20px',
      margin: '22px 0',
      fontSize: '1rem',
      lineHeight: 1.8,
    },
    note: {
      backgroundColor: isDarkMode ? `${DARK_THEME.primary}14` : '#eafafb',
      border: `1px solid ${isDarkMode ? DARK_THEME.border : '#bfe9ef'}`,
      borderRadius: '12px',
      padding: '16px 18px',
      margin: '20px 0',
      fontSize: '1rem',
    },
    tableWrap: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', margin: '14px 0', fontSize: '0.95rem' },
    th: {
      textAlign: 'left',
      border: `1px solid ${line}`,
      padding: '10px 12px',
      backgroundColor: isDarkMode ? DARK_THEME.backgroundTertiary : '#eef4f6',
      color: ink,
      verticalAlign: 'top',
    },
    td: { textAlign: 'left', border: `1px solid ${line}`, padding: '10px 12px', color: muted, verticalAlign: 'top' },
    footer: {
      marginTop: '44px',
      paddingTop: '20px',
      borderTop: `1px solid ${line}`,
      color: muted,
      fontSize: '0.9rem',
    },
    em: { color: muted },
  };

  return (
    <div style={s.page}>
      <header style={s.hero}>
        <h1 style={s.heroTitle}>Gizlilik Politikası</h1>
        <p style={s.heroMeta}>
          <strong>Turkic Silk Road</strong> mobil uygulaması · Son güncelleme: 17 Haziran 2026
        </p>
      </header>

      <div style={s.wrap}>
        <p style={s.p}>
          Bu Gizlilik Politikası, <strong>Turkic Silk Road</strong> mobil uygulamasını (“Uygulama”) kullanırken
          hangi kişisel verilerinizin işlendiğini, bu verilerin hangi amaçlarla ve hukuki sebeplerle işlendiğini,
          kimlerle paylaşıldığını ve sahip olduğunuz hakları açıklar. Uygulamayı kullanarak bu politikayı kabul
          etmiş olursunuz.
        </p>

        <div style={s.fill}>
          <strong>Veri Sorumlusu:</strong> Turkic Silk Road - Türk Devletleri Teşkilatı<br />
          <strong>Adres:</strong> Beyazıt, 34452 Fatih/İstanbul<br />
          <strong>İletişim / Başvuru:</strong>{' '}
          <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a>
        </div>

        <h2 style={s.h2}>1. Topladığımız Veriler</h2>

        <h3 style={s.h3}>a) Hesap ve profil bilgileri</h3>
        <ul style={s.ul}>
          <li style={s.li}>E-posta adresi ve şifre (kimlik doğrulama Google Firebase Authentication ile yapılır; <strong>şifreniz tarafımızca görülmez veya açık şekilde saklanmaz</strong>).</li>
          <li style={s.li}>Görünen ad / ad-soyad; isteğe bağlı: telefon, doğum tarihi, cinsiyet, profil fotoğrafı (avatar).</li>
          <li style={s.li}>Tercih edilen dil ve saat dilimi.</li>
          <li style={s.li}>İki adımlı doğrulamayı (2FA) etkinleştirirseniz buna ilişkin faktör bilgisi (Firebase tarafında).</li>
        </ul>

        <h3 style={s.h3}>b) Konum verisi</h3>
        <p style={s.p}>
          Konum yalnızca <strong>uygulama açıkken (when-in-use)</strong> ve siz ilgili bir özelliği kullandığınızda
          işlenir: yakındaki yerleri listeleme, gezi/rota planlama (TurkicRoute AI), “Buraya geldim” ziyaret kaydı ve
          haritadan konum seçme. <strong>Arka planda konum toplanmaz.</strong> İzni cihaz ayarlarından dilediğiniz zaman
          kapatabilirsiniz.
        </p>

        <h3 style={s.h3}>c) Kamera ve fotoğraf galerisi</h3>
        <p style={s.p}>
          Yalnızca siz bir işlem başlattığınızda (profil fotoğrafı yükleme veya bir yer bildirme/katkı görseli ekleme)
          kullanılır. Galeriniz veya kameranız sürekli izlenmez; yalnızca seçtiğiniz/çektiğiniz görseller işlenir.
        </p>

        <h3 style={s.h3}>d) Cihaz ve bildirim verileri</h3>
        <ul style={s.ul}>
          <li style={s.li}>Cihaza bağlı özellikler için anonim bir cihaz tanımlayıcısı.</li>
          <li style={s.li}>Bildirim izni verirseniz, anlık bildirim (push) gönderebilmek için bir bildirim jetonu (Google Firebase Cloud Messaging).</li>
        </ul>

        <h3 style={s.h3}>e) Sizin oluşturduğunuz içerik</h3>
        <p style={s.p}>
          Yorumlar ve puanlar, yer bildirimleri/düzenlemeleri, oluşturduğunuz veya kaydettiğiniz rotalar, gezi planları,
          ziyaretler, favoriler ve katkı/rozet verileri (isteğe bağlı eklediğiniz görsel ve konum dahil).
        </p>

        <h3 style={s.h3}>f) Otomatik toplanan teknik veriler</h3>
        <p style={s.p}>
          Hizmetin çalışması ve güvenliği için temel teknik kayıtlar (IP adresi, istek zamanı, hata/teşhis kayıtları).
        </p>

        <div style={s.note}>
          <strong>Önemli:</strong> Uygulamada <strong>reklam ağı, analitik veya izleme (tracking) yazılımı kullanılmaz.</strong>{' '}
          Verilerinizi reklam amacıyla satmaz veya paylaşmayız.
        </div>

        <h2 style={s.h2}>2. Verileri Hangi Amaçlarla ve Hukuki Sebeple İşliyoruz</h2>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <tbody>
              <tr><th style={s.th}>Amaç</th><th style={s.th}>Hukuki sebep (KVKK m.5 / GDPR m.6)</th></tr>
              <tr><td style={s.td}>Hesap oluşturma, giriş, e-posta doğrulama, 2FA</td><td style={s.td}>Sözleşmenin ifası; açık rıza</td></tr>
              <tr><td style={s.td}>Kaydettiğiniz içeriği (favoriler, geziler, ziyaretler, katkılar) saklama ve gösterme</td><td style={s.td}>Sözleşmenin ifası</td></tr>
              <tr><td style={s.td}>Konum, kamera/galeri, bildirim özellikleri</td><td style={s.td}>Açık rıza (cihaz izinleri ile)</td></tr>
              <tr><td style={s.td}>Güvenlik, kötüye kullanımın önlenmesi, hizmetin iyileştirilmesi</td><td style={s.td}>Meşru menfaat</td></tr>
              <tr><td style={s.td}>Yasal yükümlülüklerin yerine getirilmesi</td><td style={s.td}>Hukuki yükümlülük</td></tr>
            </tbody>
          </table>
        </div>

        <h2 style={s.h2}>3. Uygulama İzinleri</h2>
        <ul style={s.ul}>
          <li style={s.li}><strong>Konum (yalnızca kullanımda):</strong> yakındaki yerler, rota/gezi planlama, ziyaret kaydı, haritadan seçim.</li>
          <li style={s.li}><strong>Kamera:</strong> yer bildirimi veya katkı görseli için fotoğraf çekme.</li>
          <li style={s.li}><strong>Fotoğraf galerisi:</strong> profil fotoğrafı veya katkı görseli seçme.</li>
          <li style={s.li}><strong>Bildirimler:</strong> etkinlik, haber ve güncellemeler için anlık bildirim.</li>
        </ul>
        <p style={s.p}>
          Tüm izinler isteğe bağlıdır ve cihaz ayarlarından istediğiniz zaman geri alınabilir. Uygulamanın çoğu özelliği
          (harita, rotalar, etkinlikler, haberler, üye devletler) giriş yapmadan ve bu izinler olmadan da kullanılabilir.
        </p>

        <h2 style={s.h2}>4. Verilerin Paylaşıldığı Üçüncü Taraflar</h2>
        <p style={s.p}>
          Kişisel verileriniz yalnızca hizmetin sağlanması için gerekli olan aşağıdaki hizmet sağlayıcılarla,
          yalnızca gerekli ölçüde paylaşılır:
        </p>
        <ul style={s.ul}>
          <li style={s.li}><strong>Google (Firebase Authentication &amp; Cloud Messaging):</strong> kimlik doğrulama, e-posta doğrulama, iki adımlı doğrulama ve anlık bildirim jetonu.</li>
          <li style={s.li}><strong>Sunucu altyapısı:</strong> Uygulamanın verileri, hizmeti yürüten sunucularda barındırılır.</li>
          <li style={s.li}><strong>Harita ve rota hizmetleri:</strong> Açık kaynaklı harita karoları (OpenStreetMap tabanlı / CARTO) ve uydu görüntüleri (Esri) ile rota çizim hizmetleri. Haritayı kullandığınızda IP adresiniz ve görüntülenen alanın koordinatları ilgili sağlayıcılara teknik istek olarak iletilir.</li>
        </ul>
        <p style={s.p}>
          Bunların dışında verilerinizi üçüncü taraflara satmaz, kiralamaz veya pazarlama amacıyla paylaşmayız. Yasal
          olarak zorunlu hâllerde yetkili kamu kurumlarıyla paylaşım yapılabilir.
        </p>

        <h2 style={s.h2}>5. Çerezler ve İzleme</h2>
        <p style={s.p}>
          Mobil uygulama reklam çerezi veya üçüncü taraf izleme teknolojisi kullanmaz. Oturumunuzu sürdürmek için
          gerekli kimlik jetonları cihazınızda güvenli alanda (Secure Store) saklanır.
        </p>

        <h2 style={s.h2}>6. Verilerin Saklanması</h2>
        <p style={s.p}>
          Kişisel verileriniz, hesabınız aktif olduğu ve ilgili amaç için gerekli olduğu sürece saklanır. Yasal saklama
          yükümlülükleri saklıdır. Hesabınızı sildiğinizde aşağıdaki bölüm uygulanır.
        </p>

        <h2 style={s.h2}>7. Hesabınızı ve Verilerinizi Silme</h2>
        <p style={s.p}>
          Hesabınızı uygulama içinden <em style={s.em}>Hesap → Hesabı kalıcı sil</em> adımıyla veya{' '}
          <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a> üzerinden silinmesini talep ederek
          kaldırabilirsiniz. Silme işleminde:
        </p>
        <ul style={s.ul}>
          <li style={s.li}>Kişisel verileriniz (ad, e-posta, telefon, doğum tarihi vb.) silinir veya anonimleştirilir.</li>
          <li style={s.li}>Daha önce herkese açık yayınladığınız katkılar (ör. yorum/rota) topluluğun bütünlüğü için “Turkic Silk Road Kullanıcısı” olarak <strong>anonim</strong> biçimde korunabilir.</li>
          <li style={s.li}>Cihaza bağlı verileri uygulama içinden ayrıca temizleyebilirsiniz.</li>
        </ul>

        <h2 style={s.h2}>8. Veri Güvenliği</h2>
        <p style={s.p}>
          Veriler aktarım sırasında HTTPS/TLS ile şifrelenir; oturum jetonları cihazda güvenli depolama alanında tutulur;
          şifreler kimlik sağlayıcı (Firebase) tarafında özetlenmiş (hash) olarak saklanır. Hiçbir yöntem %100 güvenli
          olmasa da verilerinizi korumak için makul teknik ve idari tedbirleri uygularız.
        </p>

        <h2 style={s.h2}>9. Uluslararası Veri Aktarımı</h2>
        <p style={s.p}>
          Kimlik doğrulama ve bildirim hizmetleri (Google/Firebase) ile bazı altyapı bileşenleri, sunucuları yurt dışında
          bulunan sağlayıcılar tarafından işletilebilir. Bu aktarımlar yürürlükteki mevzuata (KVKK / GDPR) uygun şekilde
          gerçekleştirilir.
        </p>

        <h2 style={s.h2}>10. Çocukların Gizliliği</h2>
        <p style={s.p}>
          Uygulama 13 yaşın (bazı ülkelerde 16) altındaki çocuklara yönelik değildir ve bilerek çocuklardan kişisel veri
          toplamayız. Bir çocuğa ait veri işlediğimizi fark edersek sileriz.
        </p>

        <h2 style={s.h2}>11. Haklarınız</h2>
        <p style={s.p}>
          KVKK (6698 sayılı Kanun) m.11 ve GDPR kapsamında; kişisel verilerinizin işlenip işlenmediğini öğrenme,
          bilgi talep etme, düzeltme, silme/yok edilmesini isteme, işlemenin sınırlandırılmasını veya işlemeye itiraz etme,
          veri taşınabilirliği ve verdiğiniz rızayı geri çekme haklarına sahipsiniz. Taleplerinizi{' '}
          <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a> adresine iletebilirsiniz; başvurunuz
          yasal süreler içinde yanıtlanır.
        </p>

        <h2 style={s.h2}>12. Politikadaki Değişiklikler</h2>
        <p style={s.p}>
          Bu politikayı zaman zaman güncelleyebiliriz. Önemli değişiklikleri uygulama içinden veya bu sayfa üzerinden
          duyururuz; sayfa başındaki “Son güncelleme” tarihi geçerli sürümü gösterir.
        </p>

        <h2 style={s.h2}>13. İletişim</h2>
        <p style={s.p}>
          Gizlilikle ilgili tüm soru ve talepleriniz için:{' '}
          <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a>
        </p>

        <footer style={s.footer}>
          © 2026 Turkic Silk Road. Bu belge bilgilendirme amaçlıdır.
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
